import { HelpCircle } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormatPrice } from '~/utils/price';

interface Props {
  price: number;
  originalPrice?: number;
  priceWithoutVat?: number;
  currency?: string;
  vatRate?: number;
  /** Configurable products only quote a starting price — the configurator adds to it. */
  showPriceFrom?: boolean;
}

const ProductPrice: React.FC<Props> = ({
  price,
  originalPrice,
  priceWithoutVat,
  currency,
  vatRate = 21,
  showPriceFrom = false,
}) => {
  const formatPrice = useFormatPrice();
  const { t } = useTranslation();
  const hasDiscount = !!originalPrice && originalPrice > price;
  const discountPercent = hasDiscount ? Math.round((1 - price / originalPrice) * 100) : 0;
  const exclVatPrice = priceWithoutVat ?? Math.round(price / (1 + vatRate / 100));
  const effectiveVatRate =
    priceWithoutVat && priceWithoutVat > 0 ? Math.round((price / priceWithoutVat - 1) * 100) : vatRate;
  const showVatInfo = effectiveVatRate !== 21;

  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});

  const handleMouseEnter = () => {
    if (wrapperRef.current && tooltipRef.current) {
      const wRect = wrapperRef.current.getBoundingClientRect();
      const tRect = tooltipRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const margin = 16;

      // Ideal: center tooltip on icon
      let left = wRect.width / 2 - tRect.width / 2;

      // Clamp to viewport
      const absLeft = wRect.left + left;
      const absRight = absLeft + tRect.width;
      if (absLeft < margin) left += margin - absLeft;
      else if (absRight > vw - margin) left -= absRight - (vw - margin);

      // Keep arrow pointing at icon center regardless of shift
      const arrowLeft = wRect.width / 2 - left - 8;

      setTooltipStyle({ left: `${left}px` });
      setArrowStyle({ marginLeft: `${arrowLeft}px` });
    }
    setIsVisible(true);
  };

  return (
    <div className="product-price my-3 pt-2">
      {showPriceFrom && <p className="product-price__from-label mb-0">{t('components.product.price_from')}</p>}
      {hasDiscount && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="price-original text-decoration-line-through">{formatPrice(originalPrice, currency)}</span>
          <span className="price-discount">-{discountPercent}%</span>
        </div>
      )}
      <div className="d-flex align-items-center gap-1 mb-1">
        <p className="product-price-amount mb-0">
          <span>{formatPrice(price, currency)}</span>
          <span className="product-price-amount__vat-label">
            {' '}
            {t('components.product.price_incl_vat', { rate: effectiveVatRate })}
          </span>
        </p>
        {showVatInfo && (
          <span
            ref={wrapperRef}
            className="product-price__help-icon-wrapper"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setIsVisible(false)}
          >
            <HelpCircle size={20} className="flex-shrink-0 product-price__help-icon" />
            <span
              ref={tooltipRef}
              className={`product-price__tooltip${isVisible ? ' product-price__tooltip--visible' : ''}`}
              style={tooltipStyle}
            >
              <span className="product-price__tooltip-content">{t('components.product.price_vat_tooltip')}</span>
              <span className="product-price__tooltip-arrow" style={arrowStyle} />
            </span>
          </span>
        )}
      </div>
      <p className="price-excl-vat mb-0">
        {formatPrice(exclVatPrice, currency)} {t('components.product.price_excl_vat')}
      </p>
    </div>
  );
};
export default ProductPrice;
